import React, { useEffect, useMemo, useState } from "react";
import StatusDonutChart from "./StatusDonutChart";
import { BASE_URL } from "../../../config/api";
import { ValueLoader } from "../../../Component/Common/ValueLoader";
import { formatDateTimeSplit } from "../../../Component/Common/formatDateTime";
import Pagination from "../../../Component/Common/Pagination";
import { CheckCircle2, ChevronDown, ChevronUp, Clock, List, Package, TrendingUp, XCircle } from "lucide-react";
import axios from "axios";

/**
 * Retailer Warranty Earnings Dashboard
 * React + Tailwind (JavaScript) | Theme: Blue & White
 *
 * Updates added:
 * ✅ Customer Name + Customer ID
 * ✅ Provisioning & Rejected highlight (Provisioning = benefits available, earnings pending; Rejected = earnings loss)
 * ✅ Charts include Active/Provisioning/Rejected for BOTH Earnings + Count
 */

// const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

// const isRetailerMobile =
//   typeof window !== "undefined" &&
//   window.innerWidth < 640 &&
//   (authUser?.role === 5 || authUser?.role === 6);

    const glassCard =
  "glass-card";

  const normalCard =
    "bg-white border border-slate-200 shadow-sm";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function moneyINR(n) {
  const v = Number(n || 0);
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `₹${Math.round(v).toLocaleString("en-IN")}`;
  }
}

function toISODate(d) {
  return String(d || "").slice(0, 10);
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function initials(name) {
  const s = String(name || "").trim();
  if (!s) return "U";
  const parts = s.split(/\s+/).slice(0, 2);
  return parts.map((p) => (p[0] ? p[0].toUpperCase() : "")).join("");
}

function Icon({ name, className }) {
  const common = "h-5 w-5";
  switch (name) {
    case "wallet":
      return (
        <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
          <path
            d="M3.5 7.5A3.5 3.5 0 017 4h12.5a1.5 1.5 0 010 3H7A2 2 0 005 9v8a3 3 0 003 3h12.5a1.5 1.5 0 000-3H8a2 2 0 01-2-2"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path d="M16.5 12.5h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case "shield":
      return (
        <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
          <path
            d="M12 2l8 4v6c0 5.25-3.2 9.95-8 10-4.8-.05-8-4.75-8-10V6l8-4z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "chart":
      return (
        <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
          <path d="M4 19V5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M4 19h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path
            d="M7 16l3-4 3 2 4-6 2 3"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "box":
      return (
        <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
          <path d="M12 2l9 5-9 5-9-5 9-5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M21 7v10l-9 5-9-5V7" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M12 12v10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case "search":
      return (
        <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
          <path d="M10.5 18a7.5 7.5 0 117.5-7.5A7.5 7.5 0 0110.5 18z" stroke="currentColor" strokeWidth="1.8" />
          <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case "info":
      return (
        <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
          <path d="M12 22a10 10 0 110-20 10 10 0 010 20z" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 10.5v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M12 7.5h.01" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
}

function Badge({ tone = "blue", children }) {
  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

  const isRetailerMobile =
    typeof window !== "undefined" &&
    window.innerWidth < 640 &&
    (authUser?.role === 5 || authUser?.role === 6);

  const glassBadge =
    "bg-white/10 text-black border border-white/30 backdrop-blur-md";

  const tones = {
    blue: "bg-blue-50 text-blue-700 ring-1 ring-blue-200",
    green: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    amber: "bg-amber-50 text-amber-800 ring-1 ring-amber-200",
    red: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
    navy: "bg-slate-50 text-slate-700 ring-1 ring-slate-200",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all duration-300",
        isRetailerMobile ? glassBadge : tones[tone] || tones.blue
      )}
    >
      {children}
    </span>
  );
}

function StatusPill({ status }) {
  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

  const isRetailerMobile =
    typeof window !== "undefined" &&
    window.innerWidth < 640 &&
    (Number(authUser?.role) === 5 || Number(authUser?.role) === 6);

  const s = String(status || "");

  const map = {
    Active: {
      tone: "green",
      label: "Active",
      mobile:
        "bg-emerald-500/15 text-black border border-emerald-300/30 backdrop-blur-md",
    },
    Provisioning: {
      tone: "amber",
      label: "Provisioning",
      mobile:
        "bg-amber-500/15 text-black border border-amber-300/30 backdrop-blur-md",
    },
    Rejected: {
      tone: "red",
      label: "Rejected",
      mobile:
        "bg-rose-500/15 text-black border border-rose-300/30 backdrop-blur-md",
    },
  };

  const cfg = map[s] || {
    tone: "navy",
    label: s || "-",
    mobile:
      "bg-white/10 text-white border border-white/30 backdrop-blur-md",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold transition-all duration-300",
        isRetailerMobile ? cfg.mobile : ""
      )}
    >
      {isRetailerMobile ? (
        cfg.label
      ) : (
        <Badge tone={cfg.tone}>{cfg.label}</Badge>
      )}
    </span>
  );
}

const STATUS_ORDER = ["Active", "Provisioning", "Rejected"];

function sumBy(arr, key) {
  return arr.reduce((acc, it) => acc + Number(it?.[key] || 0), 0);
}

function groupBy(arr, keyFn) {
  const m = new Map();
  for (const it of arr) {
    const k = keyFn(it);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(it);
  }
  return m;
}

function KpiCard({ title, count, amount, icon, hint }) {
  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

  const isRetailerMobile =
    typeof window !== "undefined" &&
    window.innerWidth < 640 &&
    (authUser?.role === 5 || authUser?.role === 6);

  const glassCard =
    "bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]";

  return (
    <div
      className={cn(
        "rounded-2xl p-4 transition-all duration-300",
        isRetailerMobile
          ? glassCard
          : "border border-slate-200 bg-white shadow-lg"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div
            className={cn(
              "text-xs font-semibold",
              isRetailerMobile ? "text-black/70" : "text-slate-500"
            )}
          >
            {title}
          </div>

          <div className="mt-1 flex items-baseline gap-2">
            <div
              className={cn(
                "text-2xl font-semibold tracking-tight",
                isRetailerMobile ? "text-black" : "text-slate-700"
              )}
            >
              {count}
            </div>

            <div
              className={cn(
                "text-xs font-semibold",
                isRetailerMobile ? "text-black/60" : "text-slate-400"
              )}
            >
              warranties
            </div>
          </div>

          <div
            className={cn(
              "mt-1 text-sm font-semibold",
              isRetailerMobile ? "text-blue-700" : "text-blue-700"
            )}
          >
            ₹{amount}
          </div>

          {hint ? (
            <div
              className={cn(
                "mt-1 text-[11px] font-semibold",
                isRetailerMobile ? "text-black/60" : "text-slate-400"
              )}
            >
              {hint}
            </div>
          ) : null}
        </div>

        {/* ICON */}
        <div
          className={cn(
            "flex h-11 w-11 items-center justify-center rounded-xl",
            isRetailerMobile
              ? "bg-white/20 text-white border border-white/30"
              : "bg-blue-50 text-blue-700 ring-1 ring-blue-100"
          )}
        >
          <Icon
            name={icon}
            className={isRetailerMobile ? "text-blue-700" : "text-blue-700"}
          />
        </div>
      </div>
    </div>
  );
}

function Card({ title, subtitle, right, children }) {
  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

  const isRetailerMobile =
    typeof window !== "undefined" &&
    window.innerWidth < 640 &&
    (authUser?.role === 5 || authUser?.role === 6);

  const glassCard =
    "glass-card bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]";

  return (
    <div
      className={cn(
        "rounded-2xl transition-all duration-300",
        isRetailerMobile
          ? glassCard
          : "border border-slate-200 bg-white shadow-sm"
      )}
    >
      {/* HEADER */}
      <div
        className={cn(
          "flex items-start justify-between gap-3 p-5",
          isRetailerMobile
            ? "border-b border-white/20"
            : "border-b border-slate-100"
        )}
      >
        <div>
          <div
            className={cn(
              "text-sm font-semibold",
              isRetailerMobile ? "text-black" : "text-slate-700"
            )}
          >
            {title}
          </div>

          {subtitle ? (
            <div
              className={cn(
                "mt-0.5 text-xs font-semibold",
                isRetailerMobile ? "text-black/70" : "text-slate-500"
              )}
            >
              {subtitle}
            </div>
          ) : null}
        </div>

        {right}
      </div>

      {/* BODY */}
      <div
        className={cn(
          "p-5",
          isRetailerMobile ? "text-black/90" : ""
        )}
      >
        {children}
      </div>
    </div>
  );
}

function BarChart({ data, valueKey = "value", labelKey = "label", height = 220 }) {
  const maxV = Math.max(1, ...data.map((d) => Number(d[valueKey] || 0)));
  const pad = 12;
  const barW = 36;
  const gap = 18;
  const w = pad * 2 + data.length * barW + (data.length - 1) * gap;

  return (
    <div className="w-full overflow-x-auto">
      <svg width={Math.max(w, 520)} height={height} className="block">
        <line
          x1={pad}
          y1={height - 36}
          x2={Math.max(w, 520) - pad}
          y2={height - 36}
          stroke="rgb(226 232 240)"
          strokeWidth="2"
        />
        {data.map((d, i) => {
          const v = Number(d[valueKey] || 0);
          const h = Math.round(((height - 64) * v) / maxV);
          const x = pad + i * (barW + gap);
          const y = height - 36 - h;
          const label = String(d[labelKey] || "");
          const note = d.note ? String(d.note) : "";
          return (
            <g key={label + i}>
              <rect x={x} y={y} width={barW} height={h} rx={12} fill="rgb(37 99 235)" opacity="0.92" />
              <text x={x + barW / 2} y={y - 8} textAnchor="middle" fontSize="11" fill="rgb(15 23 42)" fontWeight="700">
                {v === 0 ? "0" : ""}
              </text>
              <text
                x={x + barW / 2}
                y={height - 16}
                textAnchor="middle"
                fontSize="11"
                fill="rgb(100 116 139)"
                fontWeight="700"
              >
                {label.length > 10 ? label.slice(0, 10) + "…" : label}
              </text>
              {note ? (
                <text
                  x={x + barW / 2}
                  y={height - 2}
                  textAnchor="middle"
                  fontSize="10"
                  fill="rgb(148 163 184)"
                  fontWeight="600"
                >
                  {note}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function HorizontalBars({ data, isRetailerMobile }) {

  const glassCard =
    "glass-card bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_15px_35px_rgba(31,38,135,0.25)]";

  const maxV = Math.max(1, ...data.map((d) => Number(d.value || 0)));

  return (
    <div className="w-full">
      <div className="space-y-3">
        {data.map((d) => {
          const pct = clamp((Number(d.value || 0) / maxV) * 100, 0, 100);

          return (
            <div
              key={d.label}
              className={cn(
                "rounded-2xl p-3 transition-all duration-300",
                isRetailerMobile
                  ? glassCard
                  : "bg-slate-50 ring-1 ring-slate-200"
              )}
            >
              {/* Header */}
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div
                    className={cn(
                      "text-sm font-semibold",
                      isRetailerMobile ? "text-black" : "text-slate-700"
                    )}
                  >
                    {d.label}
                  </div>

                  <div
                    className={cn(
                      "text-[11px] font-semibold",
                      isRetailerMobile
                        ? "text-black/70"
                        : "text-slate-500"
                    )}
                  >
                    {d.note}
                  </div>
                </div>

                <div
                  className={cn(
                    "shrink-0 text-sm font-semibold",
                    isRetailerMobile
                      ? "text-black"
                      : "text-blue-700"
                  )}
                >
                  {moneyINR(d.value)}
                </div>
              </div>

              {/* Progress Bar */}
              <div
                className={cn(
                  "mt-3 h-3 w-full rounded-full",
                  isRetailerMobile
                    ? "bg-white/20"
                    : "bg-white ring-1 ring-slate-200"
                )}
              >
                <div
                  className={cn(
                    "h-3 rounded-full transition-all duration-500",
                    isRetailerMobile
                      ? "bg-gradient-to-r from-blue-400 to-indigo-500"
                      : "bg-blue-600"
                  )}
                  style={{ width: pct + "%" }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────
// function moneyINR(n) {
//   const v = Number(n || 0);
//   try {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency", currency: "INR", maximumFractionDigits: 0,
//     }).format(v);
//   } catch {
//     return `₹${Math.round(v).toLocaleString("en-IN")}`;
//   }
// }

// function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }

// Shorten long product names: "Extended Warranty Android Mobile (91 Days) 5000 to 40000"
//  → "Extended Warranty · 91d · ₹5K–40K"
function shortProductName(name) {
  if (!name) return "—";
  if (name.length <= 28) return name;

  // Try to extract days + range
  const daysMatch  = name.match(/\((\d+)\s*days?\)/i);
  const rangeMatch = name.match(/(\d+)\s+to\s+(\d+)/i);
  const typeMatch  = name.match(/^([A-Za-z\s]+?)(?:\s+Android|\s+Mobile|\s+\()/i);

  const type  = typeMatch  ? typeMatch[1].trim()  : name.split(" ").slice(0, 2).join(" ");
  const days  = daysMatch  ? `${daysMatch[1]}d`   : "";
  const range = rangeMatch
    ? `₹${(Number(rangeMatch[1])/1000).toFixed(0)}K–${(Number(rangeMatch[2])/1000).toFixed(0)}K`
    : "";

  const parts = [type, days, range].filter(Boolean);
  return parts.join(" · ");
}

// Color per rank position
const RANK_COLORS = [
  { bar:"#2563EB", bg:"#EFF6FF", text:"#1D4ED8", badge:"#DBEAFE" },
  { bar:"#7C3AED", bg:"#F5F3FF", text:"#6D28D9", badge:"#EDE9FE" },
  { bar:"#059669", bg:"#ECFDF5", text:"#047857", badge:"#D1FAE5" },
  { bar:"#D97706", bg:"#FFFBEB", text:"#B45309", badge:"#FEF3C7" },
  { bar:"#DB2777", bg:"#FDF2F8", text:"#BE185D", badge:"#FCE7F3" },
  { bar:"#0891B2", bg:"#ECFEFF", text:"#0E7490", badge:"#CFFAFE" },
];
function getColor(i) { return RANK_COLORS[i % RANK_COLORS.length]; }

// ─────────────────────────────────────────────────────────────────────────────
//  ATOM: ProductRow
// ─────────────────────────────────────────────────────────────────────────────
function ProductRow({ item, rank, pct, isTop, isRetailerMobile }) {
  const [expanded, setExpanded] = useState(false);
  const color    = getColor(rank);
  const short    = shortProductName(item.label);
  const isTrunc  = short !== item.label;

  return (
    <div
      className="rounded-2xl overflow-hidden transition-all duration-200"
      style={{
        background:  isRetailerMobile ? "rgba(255,255,255,0.08)" : color.bg,
        border:      `1px solid ${isRetailerMobile ? "rgba(255,255,255,0.15)" : color.badge}`,
        boxShadow:   isTop ? `0 4px 16px ${color.bar}22` : "none",
      }}
    >
      <div className="px-4 py-3">

        {/* Top row: rank + name + amount */}
        <div className="flex items-start gap-3">

          {/* Rank badge */}
          <div
            className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black text-white mt-0.5"
            style={{ background: color.bar }}
          >
            {rank + 1}
          </div>

          {/* Name + warranty count */}
          <div className="flex-1 min-w-0">
            <p
              className="text-[13px] font-bold leading-snug"
              style={{ color: isRetailerMobile ? "#000" : color.text }}
            >
              {short}
            </p>
            {isTrunc && (
              <button
                onClick={() => setExpanded(v => !v)}
                className="flex items-center gap-0.5 text-[10px] font-semibold mt-0.5 opacity-60 hover:opacity-100 transition-opacity"
                style={{ color: color.bar }}
              >
                {expanded ? <><ChevronUp size={9}/> less</> : <><ChevronDown size={9}/> full name</>}
              </button>
            )}
            {expanded && (
              <p className="text-[11px] mt-1 leading-snug opacity-70"
                style={{ color: isRetailerMobile ? "#000" : color.text }}>
                {item.label}
              </p>
            )}
            <p
              className="text-[11px] font-semibold mt-0.5"
              style={{ color: isRetailerMobile ? "rgba(0,0,0,0.6)" : color.bar + "BB" }}
            >
              {item.note}
            </p>
          </div>

          {/* Amount */}
          <div className="flex-shrink-0 text-right">
            <p
              className="text-[14px] font-black"
              style={{ color: isRetailerMobile ? "#1D4ED8" : color.bar }}
            >
              {moneyINR(item.value)}
            </p>
            {isTop && (
              <span
                className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded-full"
                style={{ background: color.badge, color: color.text }}
              >
                <TrendingUp size={8}/> Top
              </span>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div
          className="mt-2.5 h-2 rounded-full overflow-hidden"
          style={{ background: isRetailerMobile ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.6)" }}
        >
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width:      `${pct}%`,
              background: isRetailerMobile
                ? `linear-gradient(90deg, #60A5FA, #818CF8)`
                : `linear-gradient(90deg, ${color.bar}CC, ${color.bar})`,
            }}
          />
        </div>

        {/* Pct label */}
        <p
          className="text-[10px] font-semibold mt-1 text-right"
          style={{ color: isRetailerMobile ? "rgba(0,0,0,0.5)" : color.bar + "99" }}
        >
          {Math.round(pct)}% of total
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ATOM: Skeleton
// ─────────────────────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {[100, 75, 55, 40, 30].map((w, i) => (
        <div key={i} className="rounded-2xl bg-slate-100 p-4 space-y-2.5">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded-full bg-slate-200"/>
            <div className="flex-1 space-y-1.5">
              <div className="h-3 rounded bg-slate-200" style={{ width:`${w}%` }}/>
              <div className="h-2.5 rounded bg-slate-200" style={{ width:`${w*0.6}%` }}/>
            </div>
            <div className="w-14 h-4 rounded bg-slate-200"/>
          </div>
          <div className="h-2 rounded-full bg-slate-200"/>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ORGANISM: ProductWiseEarnings
//  Drop-in replacement for the Card + HorizontalBars combo
// ─────────────────────────────────────────────────────────────────────────────
function ProductWiseEarnings({ data = [], loading = false, isRetailerMobile = false }) {
 

  const sorted = useMemo(
    () => [...data].sort((a, b) => Number(b.value) - Number(a.value)),
    [data]
  );
  const maxVal    = Math.max(1, ...sorted.map(d => Number(d.value || 0)));
  const totalVal  = sorted.reduce((s, d) => s + Number(d.value || 0), 0);

  const ITEMS_PER_VIEW = 2;
const [startIndex, setStartIndex] = useState(0);

const visible = sorted.slice(startIndex, startIndex + ITEMS_PER_VIEW);

const canGoLeft = startIndex > 0;
const canGoRight = startIndex + ITEMS_PER_VIEW < sorted.length;

const handleNext = () => {
  if (canGoRight) {
    setStartIndex(prev => prev + ITEMS_PER_VIEW);
  }
};

const handlePrev = () => {
  if (canGoLeft) {
    setStartIndex(prev => prev - ITEMS_PER_VIEW);
  }
};
  

  const glassHeader = "bg-white/10 backdrop-blur-xl border-b border-white/20";
  const plainHeader = "bg-white border-b border-slate-100";

  return (
    <div
      className="rounded-2xl overflow-hidden transition-all duration-300"
      style={{
        background: isRetailerMobile
          ? "rgba(255,255,255,0.06)"
          : "#fff",
        border:     isRetailerMobile
          ? "1px solid rgba(255,255,255,0.18)"
          : "1px solid #E2E8F0",
        boxShadow:  isRetailerMobile
          ? "0 20px 50px rgba(31,38,135,0.25)"
          : "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      {/* ── HEADER ── */}
      <div className={`flex items-center justify-between px-5 py-4 ${isRetailerMobile ? glassHeader : plainHeader}`}>
        <div>
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-xl flex items-center justify-center"
              style={{ background: isRetailerMobile ? "rgba(255,255,255,0.15)" : "#EFF6FF" }}
            >
              <Package size={14} style={{ color: isRetailerMobile ? "#fff" : "#2563EB" }}/>
            </div>
            <span
              className="text-sm font-bold"
              style={{ color: isRetailerMobile ? "#000" : "#1E293B" }}
            >
              Product-wise Earnings
            </span>
          </div>
          <p
            className="text-[11px] font-semibold mt-0.5 ml-9"
            style={{ color: isRetailerMobile ? "rgba(0,0,0,0.6)" : "#94A3B8" }}
          >
            {sorted.length} products · {moneyINR(totalVal)} total
          </p>
        </div>

        <div
          className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold"
          style={{
            background: isRetailerMobile ? "rgba(255,255,255,0.15)" : "#F1F5F9",
            color:      isRetailerMobile ? "#000" : "#475569",
          }}
        >
          <TrendingUp size={11}/>
          Ranking
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="p-4 space-y-2.5">
        {loading ? (
          <Skeleton />
        ) : sorted.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-12 rounded-2xl"
            style={{ background: isRetailerMobile ? "rgba(255,255,255,0.06)" : "#F8FAFC" }}
          >
            <Package size={28} style={{ color: isRetailerMobile ? "rgba(255,255,255,0.3)" : "#CBD5E1" }}/>
            <p className="text-sm font-semibold mt-2"
              style={{ color: isRetailerMobile ? "rgba(0,0,0,0.5)" : "#94A3B8" }}>
              No data found
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">

              {/* LEFT BUTTON */}
              <button
                onClick={handlePrev}
                disabled={!canGoLeft}
                className="p-2 rounded-xl border disabled:opacity-30"
              >
                ⬅️
              </button>

              {/* CARDS */}
              <div className="flex-1 grid grid-cols-1 gap-2">
                {visible.map((item, i) => (
                  <ProductRow
                    key={item.label + i}
                    item={item}
                    rank={startIndex + i}
                    pct={clamp((Number(item.value) / maxVal) * 100, 2, 100)}
                    isTop={startIndex + i === 0}
                    isRetailerMobile={isRetailerMobile}
                  />
                ))}
              </div>

              {/* RIGHT BUTTON */}
              <button
                onClick={handleNext}
                disabled={!canGoRight}
                className="p-2 rounded-xl border disabled:opacity-30"
              >
                ➡️
              </button>

            </div>

            {/* Show more / less */}
            {/* {sorted.length > SHOW_INITIAL && (
              <button
                onClick={() => setShowAll(v => !v)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl text-[12px] font-bold transition-all duration-150"
                style={{
                  background: isRetailerMobile ? "rgba(255,255,255,0.08)" : "#F8FAFC",
                  border:     isRetailerMobile ? "1px solid rgba(255,255,255,0.15)" : "1px dashed #CBD5E1",
                  color:      isRetailerMobile ? "#000" : "#64748B",
                }}
              >
                {showAll ? (
                  <><ChevronUp size={14}/> Show Less</>
                ) : (
                  <><ChevronDown size={14}/> Show {remaining} more products</>
                )}
              </button>
            )} */}
          </>
        )}
      </div>
    </div>
  );
}

function StatusHelp() {
  return (
    <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
          <Icon name="info" className="text-blue-700" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-700">Status meaning</div>
          <div className="mt-1 text-xs font-semibold text-slate-600 leading-relaxed">
            <span className="font-semibold text-amber-700">Provisioning</span> = Customer can avail benefits, but earnings are pending. •{" "}
            <span className="font-semibold text-rose-700">Rejected</span> = Warranty rejected, you lose earnings.
          </div>
        </div>
      </div>
    </div>
  );
}


/* ================= PRODUCT SKELETON ================= */

function ProductBarsSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-200"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="space-y-1">
              <div className="h-4 w-32 rounded bg-slate-200" />
              <div className="h-3 w-24 rounded bg-slate-200" />
            </div>
            <div className="h-4 w-16 rounded bg-slate-200" />
          </div>

          <div className="mt-2 h-3 w-full rounded-full bg-white ring-1 ring-slate-200">
            <div className="h-3 w-1/2 rounded-full bg-slate-200" />
          </div>
        </div>
      ))}
    </div>
  );
}


export default function RetailerWarrantyEarningsDashboard() {

  const [isRetailerMobile, setIsRetailerMobile] = useState(false);

  useEffect(() => {
  const checkRetailerTheme = () => {
    const authUser = JSON.parse(
      localStorage.getItem("authUserData") || "{}"
    );

    const isMobile =
      window.innerWidth < 640 &&
      (Number(authUser?.role) === 5 ||
        Number(authUser?.role) === 6);

    setIsRetailerMobile(isMobile);
  };

  // run immediately
  checkRetailerTheme();

  // listen resize
  window.addEventListener("resize", checkRetailerTheme);

  return () => {
    window.removeEventListener("resize", checkRetailerTheme);
  };
}, []);

  const [apiSummary, setApiSummary] = useState({
  total: { count: 0, payout: 0 },
  active: { count: 0, payout: 0 },
  provisioning: { count: 0, payout: 0 },
  rejected: { count: 0, payout: 0 },
});

const [statusDonutData, setStatusDonutData] = useState({
  Active: 0,
  Provisioning: 0,
  Rejected: 0,
});

const [productChart, setProductChart] = useState([]);

const [loading, setLoading] = useState(true);

const [page, setPage] = useState(1);
const [totalPages, setTotalPages] = useState(1);
const [debouncedSearch, setDebouncedSearch] = useState("");





const STATUS_ICONS = {
  ALL: <List size={16} />,
  Active: <CheckCircle2 size={16} />,
  Provisioning: <Clock size={16} />,
  Rejected: <XCircle size={16} />,
};


  // const [rows] = useState(MOCK_WARRANTIES);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  useEffect(() => {

    const authUser = JSON.parse(
      localStorage.getItem("authUserData") || "{}"
    );

    const role =
      Number(authUser.role);

    let params =
      new URLSearchParams();

    // ROLE PARAM
    if (role === 6)
      params.append(
        "promoter_id",
        authUser.id
      );

    if (role === 5)
      params.append(
        "retailer_id",
        authUser.id
      );

    if (role === 4)
      params.append(
        "agent_id",
        authUser.id
      );

    if (role === 2)
      params.append(
        "company_id",
        authUser.id
      );

    // STATUS PARAM
    if (
      statusFilter ===
      "Active"
    ) {
      params.append(
        "is_approved",
        1
      );
    }

    if (
      statusFilter ===
      "Provisioning"
    ) {
      params.append(
        "is_approved",
        0
      );
    }

    if (
      statusFilter ===
      "Rejected"
    ) {
      params.append(
        "is_approved",
        2
      );
    }

    // SEARCH PARAM
    if (debouncedSearch) {
      params.append(
        "search",
        debouncedSearch
      );
    }

    params.append(
      "page",
      page
    );

    if (!params.toString())
      return;

    setLoading(true);

    axios
      .get(
        `${BASE_URL}warranty/get-devices?${params.toString()}`,
        {
          headers: {
            Accept:
              "application/json",

            "Content-Type":
              "application/json",
          },
        }
      )

      .then((response) => {

        const res =
          response.data;

        const list =
          res?.data || [];

        const getEarningsByRole = (
          device,
          role
        ) => {

          if (role === 6)
            return (
              device.employee_payout || 0
            );

          if (role === 5)
            return (
              device.retailer_payout || 0
            );

          if (role === 4)
            return (
              device.other_payout || 0
            );

          if (role === 2)
            return (
              device.company_payout || 0
            );

          return 0;
        };

        const mapped =
          list.map((d) => ({
            id: d.id,

            warrantyId:
              d.w_code,

            customerId:
              d.customer
                ?.c_code || "-",

            customerName:
              d.customer
                ?.name || "-",

            productName:
              d.product_name || "-",

            categoryname:
              d.category_name || "-",

            dateOfSale:
              formatDateTimeSplit(
                d.created_at
              ),

            warrantyPrice:
              d.product_mrp || 0,

            earnings:
              getEarningsByRole(
                d,
                role
              ),

            status:
              d.is_approved === 1
                ? "Active"
                : d.is_approved === 2
                ? "Rejected"
                : "Provisioning",

            rejectremark:
              d.reject_remark,

            note:
              d.note,
          }));

        setRows(mapped);

        setTotalPages(
          res.last_page || 1
        );
      })

      .catch((err) => {

        console.error(err);

      })

      .finally(() =>
        setLoading(false)
      );

  }, [
    page,
    statusFilter,
    debouncedSearch,
  ]);

useEffect(() => {
  const timer = setTimeout(() => {
    if (q.length >= 4) {
      setDebouncedSearch(q);
    } else if (q.length === 0) {
      setDebouncedSearch("");
    }
  }, 500);

  return () => clearTimeout(timer);
}, [q]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows
      .filter((r) => (statusFilter === "ALL" ? true : r.status === statusFilter))
      .filter((r) => {
        if (!term) return true;
        const hay = [r.warrantyId, r.customerId, r.customerName, r.productName, r.status, r.dateOfSale].join(" ").toLowerCase();
        return hay.includes(term);
      });
  }, [rows, q, statusFilter]);

  const totals = useMemo(() => {
    const totalCount = rows.length;
    const totalEarnings = sumBy(rows, "earnings");
    const byStatus = {};
    for (const s of STATUS_ORDER) {
      const list = rows.filter((r) => r.status === s);
      byStatus[s] = { count: list.length, earnings: sumBy(list, "earnings") };
    }
    return { totalCount, totalEarnings, byStatus };
  }, [rows]);

  const statusEarningsChart = useMemo(() => {
    return STATUS_ORDER.map((s) => ({
      label: s,
      value: totals.byStatus[s]?.earnings || 0,
      note: `${totals.byStatus[s]?.count || 0}w`,
    }));
  }, [totals]);

  const statusCountChart = useMemo(() => {
    return STATUS_ORDER.map((s) => ({
      label: s,
      value: totals.byStatus[s]?.count || 0,
      note: moneyINR(totals.byStatus[s]?.earnings || 0),
    }));
  }, [totals]);

useEffect(() => {

  const authUser = JSON.parse(
    localStorage.getItem("authUserData") || "{}"
  );

  const role =
    authUser.role;

  let params = {};

  if (role === 5) {

    params = {
      retailer_id:
        authUser.id,

      company_id:
        authUser.company_id,
    };

  } else if (role === 4) {

    params = {
      agent_id:
        authUser.id,

      company_id:
        authUser.company_id,
    };

  } else if (role === 2) {

    params = {
      company_id:
        authUser.id,
    };

  } else if (role === 6) {

    params = {
      promoter_id:
        authUser.id,

      company_id:
        authUser.company_id,
    };
  }

  if (!params.company_id) {

    setLoading(false);

    return;
  }

  axios
    .get(
      `${BASE_URL}warranty/earning-dashbard`,
      {
        params,

        headers: {
          Accept:
            "application/json",

          "Content-Type":
            "application/json",
        },
      }
    )

    .then((response) => {

      const res =
        response.data;

      const d =
        res?.data;

      if (!d) return;

      setApiSummary(
        d.summary
      );

      const map = {};

      d.status_chart?.forEach(
        (s) => {
          map[s.label] =
            s.count;
        }
      );

      setStatusDonutData({
        Active:
          map.Active || 0,

        Provisioning:
          map.Provisioning || 0,

        Rejected:
          map.Rejected || 0,
      });

      setProductChart(
        d.product_wise_earnings?.map(
          (p) => ({
            label:
              p.product_name,

            value:
              p.total_payout,

            note:
              `${p.warranty_count} warranties`,
          })
        ) || []
      );
    })

    .catch((err) => {

      console.error(err);

    })

    .finally(() =>
      setLoading(false)
    );

}, []);



  // const productChart = useMemo(() => {
  //   const g = groupBy(rows, (r) => r.productName);
  //   const out = [];
  //   for (const [product, list] of g.entries()) {
  //     out.push({ label: product, value: sumBy(list, "earnings"), note: `${list.length} warranties` });
  //   }
  //   out.sort((a, b) => b.value - a.value);
  //   return out;
  // }, [rows]);

  const filteredMeta = useMemo(() => {
    return { count: filtered.length, earnings: sumBy(filtered, "earnings"), price: sumBy(filtered, "warrantyPrice") };
  }, [filtered]);

  return (
      <div className="mx-auto">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            title="Total Warranty"
            count={loading ? <ValueLoader/> : apiSummary.total.count}
            amount={loading ? <ValueLoader/> : apiSummary.total.payout}
            icon="shield"
            hint="All statuses"
            
          />

          <KpiCard
            title="Active"
            count={loading ? <ValueLoader/> : apiSummary.active.count}
            amount={loading ? <ValueLoader/> : apiSummary.active.payout}
            icon="wallet"
            hint="Earnings credited"
          />

          <KpiCard
            title="Provisioning"
            count={loading ? <ValueLoader/> : apiSummary.provisioning.count}
            amount={loading ? <ValueLoader/> : apiSummary.provisioning.payout}
            icon="box"
            hint="Benefits available • Earnings pending"
          />

          <KpiCard
            title="Rejected"
            count={loading ? <ValueLoader/> : apiSummary.rejected.count}
            amount={loading ? <ValueLoader/> : apiSummary.rejected.payout}
            icon="shield"
            hint="Rejected • Earnings loss"
          />

        </div>

        

        <div className="mt-5 grid gap-4 lg:grid-cols-5">
          <div className="lg:col-span-3 ">
            {/* <Card
              title="Status-wise (Earnings + Count)"
              subtitle="Active / Provisioning / Rejected"
              right={
                <Badge tone="navy">
                  <Icon name="chart" className="h-4 w-4" />
                  Charts
                </Badge>
              }
            >
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <StatusPill status="Active" />
                <StatusPill status="Provisioning" />
                <StatusPill status="Rejected" />
              </div>

              <div className="space-y-4">
                <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
                  <div className="mb-2 text-xs font-semibold text-slate-700">Earnings (₹)</div>
                  <BarChart data={statusEarningsChart} valueKey="value" labelKey="label" height={210} />
                </div>

                <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
                  <div className="mb-2 text-xs font-semibold text-slate-700">Count (warranties)</div>
                  <BarChart data={statusCountChart} valueKey="value" labelKey="label" height={210} />
                </div>
              </div>
            </Card> */}
            <StatusDonutChart mode="count" data={statusDonutData} loading={loading} />

          </div>

          {/* <div className="lg:col-span-2">
            <Card
              title="Product-wise Earnings"
              subtitle="Which product gives more earnings"
              right={
                <Badge tone="navy">
                  <Icon name="box" className="h-4 w-4" />
                  Ranking
                </Badge>
              }
            >
              {loading ? (
                <ProductBarsSkeleton />
              ) : productChart.length === 0 ? (
                <div className="flex h-40 items-center justify-center text-sm text-slate-500">
                  No data found
                </div>
              ) : (
                <HorizontalBars data={productChart} />
              )}
            </Card>
          </div> */}
          <div className="lg:col-span-2">
            <ProductWiseEarnings data={productChart} loading={loading} isRetailerMobile={isRetailerMobile}/>
          </div>
          
        </div>

        <div
            className={cn(
              "mt-5 rounded-2xl p-4 transition-all duration-300",
              isRetailerMobile
                ? glassCard
                : "border border-slate-200 bg-white shadow-sm"
            )}
          >
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-2xl transition",
                  isRetailerMobile
                    ? "bg-white/10 text-white ring-1 ring-white/30"
                    : "bg-blue-50 text-blue-700 ring-1 ring-blue-100"
                )}
              >
                <Icon name="search" className="text-blue-700" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-700">Search</div>
                <div className="text-xs text-slate-500">Warranty ID / Customer / Product / Status</div>
              </div>
            </div>

            <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row lg:items-center lg:justify-end">
              <div className="relative w-full lg:w-[360px]">
                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Icon name="search" className="h-4 w-4" />
                </div>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search warranty / customer..."
                  className={cn(
                    "w-full rounded-2xl px-10 py-2.5 text-sm font-semibold outline-none transition",
                    isRetailerMobile
                      ? "bg-white/10 border border-white/30 text-black placeholder:text-black/60 focus:ring-2 focus:ring-white/40"
                      : "border border-slate-200 bg-white text-slate-700 placeholder:text-slate-400 focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                  )}
                />
              </div>

             {/* STATUS FILTER PILLS */}
              <div className="flex items-center gap-2 overflow-x-auto">
                {["ALL", "Active", "Provisioning", "Rejected"].map((status) => {
                  const isActive = statusFilter === status;

                  const activeClasses =
                    status === "ALL"
                      ? "bg-blue-600 text-white border-blue-600"
                      : status === "Active"
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : status === "Provisioning"
                      ? "bg-amber-500 text-white border-amber-500"
                      : status === "Rejected"
                      ? "bg-rose-600 text-white border-rose-600"
                      : "";

                  return (
                    <button
                      key={status}
                      onClick={() => {
                        setStatusFilter(status);
                        setPage(1);
                      }}
                      className={cn(
                        "px-3 sm:px-4 py-2 rounded-full text-sm font-semibold border transition flex items-center justify-center",
                        isActive
                          ? activeClasses
                          : isRetailerMobile
                          ? "bg-white/10 text-black border-white/30 hover:bg-white/20"
                          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                      )}
                    >
                      {/* Mobile: icon only */}
                      <span className="sm:hidden">
                        {STATUS_ICONS[status]}
                      </span>

                      {/* Desktop: text */}
                      <span className="hidden sm:inline">
                        {status}
                      </span>
                    </button>
                  );
                })}
              </div>
              
              {/* <div className="rounded-2xl bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-200">
                {filteredMeta.count} shown • <span className="text-blue-700">{moneyINR(filteredMeta.earnings)}</span>
              </div> */}
            </div>
          </div>
        </div>

       <div
          className={cn(
            "mt-5 rounded-2xl transition-all duration-300",
            isRetailerMobile
              ? "bg-white/10 backdrop-blur-xl border border-white/30 shadow-[0_15px_35px_rgba(31,38,135,0.25)]"
              : "border border-slate-200 bg-white shadow-sm"
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-5">
            <div>
              <div className="text-sm font-semibold text-slate-700">Warranty Earnings List</div>
              {/* <div className="mt-0.5 text-xs font-semibold text-slate-500">
                Warranty ID • Customer • Product • Sale date • Status • Price • Earnings
              </div> */}
            </div>
           <Badge tone="blue">
              <Icon name="shield" className="h-4 w-4" />
              <div className="flex items-center whitespace-nowrap">
                {filtered.length} Records
              </div>
            </Badge>
          </div>

          <div className="hidden lg:block md:hidden overflow-auto">
            <table className=" w-full">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <Th>Warranty ID</Th>
                  <Th>Customer</Th>
                  <Th>Product</Th>
                  <Th>Date of Sale</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Warranty Price</Th>
                  <Th className="text-right">Earnings</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    className={cn(
                      "border-t hover:bg-slate-50/60",
                      r.status === "Provisioning" ? "border-amber-100 bg-amber-50/40" : "border-slate-100",
                      r.status === "Rejected" ? "border-rose-100 bg-rose-50/40" : ""
                    )}
                  >
                    <Td>
                      <div className="text-sm font-semibold text-slate-700">{r.warrantyId}</div>
                      <div
                        className={cn(
                          "text-[11px] font-semibold text-nowrap",
                          r.status === "Rejected" ? "text-rose-600" : r.status === "Provisioning" ? "text-amber-700" : "text-slate-400"
                        )}
                      >
                        {r.status === "Provisioning"
                          ? "Benefits available • Earnings pending"
                          : r.status === "Rejected"
                          ? `${r.rejectremark || ""}${r.rejectremark && r.note ? " • " : ""}${r.note || ""}`
                          : "ARP"}
                      </div>
                    </Td>

                    <Td>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-800 font-semibold ring-1 ring-slate-200">
                          {initials(r.customerName)}
                        </span>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-slate-700">{r.customerName}</div>
                          <div className="text-[11px] font-semibold text-slate-400">{r.customerId}</div>
                        </div>
                      </div>
                    </Td>

                    <Td>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-blue-600 text-white font-semibold ">
                          {initials(r.productName)}
                        </span>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-slate-700 text-wrap">{r.productName}</div>
                          <div className="text-[11px] font-semibold text-slate-400">{r.categoryname}</div>
                        </div>
                      </div>
                    </Td>

                    <Td>
                      <div className="text-sm font-semibold text-slate-700">{r.dateOfSale.date}</div>
                      <div className="text-[11px] font-semibold text-slate-400">{r.dateOfSale.time}</div>
                    </Td>

                    <Td>
                      <StatusPill status={r.status} />
                    </Td>

                    <Td className="text-right">
                      <div className="text-sm font-semibold text-slate-700">{moneyINR(r.warrantyPrice)}</div>
                      <div className="text-[11px] font-semibold text-slate-400">MRP</div>
                    </Td>

                    <Td className="text-right">
                      <div
                        className={cn(
                          "text-sm font-semibold",
                          r.status === "Rejected"
                            ? "text-rose-700"
                            : r.status === "Provisioning"
                            ? "text-amber-800"
                            : r.earnings > 0
                            ? "text-blue-700"
                            : "text-slate-500"
                        )}
                      >
                        {moneyINR(r.earnings)}
                      </div>
                      <div
                        className={cn(
                          "text-[11px] font-semibold",
                          r.status === "Rejected" ? "text-rose-600" : r.status === "Provisioning" ? "text-amber-700" : "text-slate-400"
                        )}
                      >
                        {r.status === "Provisioning" ? "Pending" : r.status === "Rejected" ? "Lost" : "Earning"}
                      </div>
                    </Td>
                  </tr>
                ))}

                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-10">
                      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
                        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white">
                          <Icon name="search" className="text-white" />
                        </div>
                        <div className="text-sm font-semibold text-slate-700">No records found</div>
                        <div className="mt-1 text-xs font-semibold text-slate-500">Try different search or filter.</div>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {/* ================= MOBILE CARDS ================= */}
          <div className="lg:hidden p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((r) => (
            <div
              key={r.id}
              className={cn(
                "rounded-2xl p-4 transition-all duration-300",
                isRetailerMobile
                  ? "bg-white/10 backdrop-blur-xl border border-white/30 shadow-[0_15px_35px_rgba(31,38,135,0.25)]"
                  : "rounded-xl border p-4 shadow-lg",

                !isRetailerMobile &&
                  r.status === "Provisioning" &&
                  "border-amber-200 bg-amber-50/40",

                !isRetailerMobile &&
                  r.status === "Rejected" &&
                  "border-rose-200 bg-rose-50/40",

                !isRetailerMobile &&
                  r.status === "Active" &&
                  "border-slate-200 bg-green-50/40"
              )}
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className={cn(
                    "text-sm font-semibold",
                    isRetailerMobile ? "text-black" : "text-slate-800"
                  )}>
                    {r.warrantyId}
                  </div>
                  <div className={cn(
                    "text-xs font-semibold",
                    isRetailerMobile ? "text-black/70" : "text-slate-500"
                  )}>
                    {r.dateOfSale.date} {r.dateOfSale.time}
                  </div>
                </div>
                <StatusPill status={r.status} />
              </div>

              {/* Customer */}
              <div className="mt-3 flex items-center gap-3">
                <div className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-xl text-sm font-bold",
                  isRetailerMobile
                    ? "bg-white/20 text-black border border-white/30"
                    : "bg-slate-100 text-slate-700 ring-1 ring-slate-200"
                )}>
                  {initials(r.customerName)}
                </div>
                <div className="min-w-0">
                  <div className={cn(
                    "truncate text-sm font-semibold",
                    isRetailerMobile ? "text-black" : "text-slate-700"
                  )}>
                    {r.customerName}
                  </div>
                  <div className={cn(
                    "text-xs font-semibold",
                    isRetailerMobile ? "text-black/70" : "text-slate-400"
                  )}>
                    {r.customerId}
                  </div>
                </div>
              </div>

              {/* Product */}
              <div className="mt-3 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-xs font-bold text-white">
                  {initials(r.productName)}
                </div>
                <div>
                  <div className={cn(
                    "text-sm font-semibold",
                    isRetailerMobile ? "text-black" : "text-slate-700"
                  )}>
                    {r.productName}
                  </div>
                  <div className={cn(
                    "text-[11px] font-semibold",
                    isRetailerMobile ? "text-black/70" : "text-slate-400"
                  )}>
                    {r.categoryname}
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className={cn(
                "my-3 h-px",
                isRetailerMobile ? "bg-white/20" : "bg-slate-200"
              )} />

              {/* Price + Earnings */}
              <div className="flex items-center justify-between">
                <div>
                  <div className={cn(
                    "text-xs font-semibold",
                    isRetailerMobile ? "text-black/70" : "text-slate-500"
                  )}>
                    Warranty Price
                  </div>
                  <div className={cn(
                    "text-sm font-semibold",
                    isRetailerMobile ? "text-black" : "text-slate-800"
                  )}>
                    {moneyINR(r.warrantyPrice)}
                  </div>
                </div>

                <div className="text-right">
                  <div className={cn(
                    "text-xs font-semibold",
                    isRetailerMobile ? "text-black/70" : "text-slate-500"
                  )}>
                    Earnings
                  </div>
                  <div
                    className={cn(
                      "text-sm font-semibold",
                      r.status === "Rejected"
                        ? "text-rose-600"
                        : r.status === "Provisioning"
                        ? "text-amber-600"
                        : isRetailerMobile
                        ? "text-blue-700"
                        : "text-blue-700"
                    )}
                  >
                    {moneyINR(r.earnings)}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {filtered.length === 0 && (
            <div className={cn(
              "rounded-2xl p-8 text-center",
              isRetailerMobile
                ? "bg-white/10 backdrop-blur-xl border border-white/30 text-black"
                : "border border-dashed border-slate-200 bg-slate-50"
            )}>
              <div className="text-sm font-semibold">
                No records found
              </div>
              <div className="mt-1 text-xs opacity-70">
                Try different search or filter.
              </div>
            </div>
          )}
        </div>

          {totalPages > 1 && (
              <div className="flex justify-end py-2">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            )}
        </div>

        {/* <div className="mt-6 text-center text-xs font-semibold text-slate-400">
          Tip: Replace MOCK_WARRANTIES with API response ({"{ warrantyId, customerId, customerName, productName, dateOfSale, status, warrantyPrice, earnings }"}).
        </div> */}
      </div>
  );
}

function Th({ children, className }) {
  return <th className={cn("px-5 py-3 text-xs font-semibold text-slate-600 text-nowrap", className)}>{children}</th>;
}

function Td({ children, className }) {
  return <td className={cn("px-5 py-4 align-middle text-nowrap", className)}>{children}</td>;
}


// import React, { useEffect, useMemo, useState } from "react";
// import StatusDonutChart from "./StatusDonutChart";
// import { BASE_URL } from "../../../config/api";
// import { ValueLoader } from "../../../Component/Common/ValueLoader";

// /**
//  * Retailer Warranty Earnings Dashboard
//  * React + Tailwind (JavaScript) | Theme: Blue & White
//  *
//  * Updates added:
//  * ✅ Customer Name + Customer ID
//  * ✅ Provisioning & Rejected highlight (Provisioning = benefits available, earnings pending; Rejected = earnings loss)
//  * ✅ Charts include Active/Provisioning/Rejected for BOTH Earnings + Count
//  */

// function cn(...classes) {
//   return classes.filter(Boolean).join(" ");
// }

// function moneyINR(n) {
//   const v = Number(n || 0);
//   try {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//       maximumFractionDigits: 0,
//     }).format(v);
//   } catch {
//     return `₹${Math.round(v).toLocaleString("en-IN")}`;
//   }
// }

// function toISODate(d) {
//   return String(d || "").slice(0, 10);
// }

// function clamp(n, a, b) {
//   return Math.max(a, Math.min(b, n));
// }

// function initials(name) {
//   const s = String(name || "").trim();
//   if (!s) return "U";
//   const parts = s.split(/\s+/).slice(0, 2);
//   return parts.map((p) => (p[0] ? p[0].toUpperCase() : "")).join("");
// }

// function Icon({ name, className }) {
//   const common = "h-5 w-5";
//   switch (name) {
//     case "wallet":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path
//             d="M3.5 7.5A3.5 3.5 0 017 4h12.5a1.5 1.5 0 010 3H7A2 2 0 005 9v8a3 3 0 003 3h12.5a1.5 1.5 0 000-3H8a2 2 0 01-2-2"
//             stroke="currentColor"
//             strokeWidth="1.8"
//             strokeLinejoin="round"
//           />
//           <path d="M16.5 12.5h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//         </svg>
//       );
//     case "shield":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path
//             d="M12 2l8 4v6c0 5.25-3.2 9.95-8 10-4.8-.05-8-4.75-8-10V6l8-4z"
//             stroke="currentColor"
//             strokeWidth="1.8"
//             strokeLinejoin="round"
//           />
//         </svg>
//       );
//     case "chart":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path d="M4 19V5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//           <path d="M4 19h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//           <path
//             d="M7 16l3-4 3 2 4-6 2 3"
//             stroke="currentColor"
//             strokeWidth="1.8"
//             strokeLinecap="round"
//             strokeLinejoin="round"
//           />
//         </svg>
//       );
//     case "box":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path d="M12 2l9 5-9 5-9-5 9-5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
//           <path d="M21 7v10l-9 5-9-5V7" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
//           <path d="M12 12v10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//         </svg>
//       );
//     case "search":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path d="M10.5 18a7.5 7.5 0 117.5-7.5A7.5 7.5 0 0110.5 18z" stroke="currentColor" strokeWidth="1.8" />
//           <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//         </svg>
//       );
//     case "info":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path d="M12 22a10 10 0 110-20 10 10 0 010 20z" stroke="currentColor" strokeWidth="1.8" />
//           <path d="M12 10.5v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//           <path d="M12 7.5h.01" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
//         </svg>
//       );
//     default:
//       return null;
//   }
// }

// function Badge({ tone = "blue", children }) {
//   const tones = {
//     blue: "bg-blue-50 text-blue-700 ring-blue-200",
//     green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
//     amber: "bg-amber-50 text-amber-800 ring-amber-200",
//     red: "bg-rose-50 text-rose-700 ring-rose-200",
//     navy: "bg-slate-50 text-slate-700 ring-slate-200",
//   };
//   return (
//     <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1", tones[tone] || tones.blue)}>
//       {children}
//     </span>
//   );
// }

// function StatusPill({ status }) {
//   const s = String(status || "");
//   const map = {
//     Active: { tone: "green", label: "Active" },
//     Provisioning: { tone: "amber", label: "Provisioning" },
//     Rejected: { tone: "red", label: "Rejected" },
//   };
//   const cfg = map[s] || { tone: "navy", label: s || "-" };
//   return <Badge tone={cfg.tone}>{cfg.label}</Badge>;
// }

// const MOCK_WARRANTIES = [
//   {
//     warrantyId: "WRT-120045",
//     customerId: "CUST-90011",
//     customerName: "Amit Patil",
//     productName: "Screen Protection",
//     dateOfSale: "2026-01-02",
//     status: "Active",
//     warrantyPrice: 499,
//     earnings: 160,
//   },
//   {
//     warrantyId: "WRT-120046",
//     customerId: "CUST-90012",
//     customerName: "Rahul Sharma",
//     productName: "Extended Warranty",
//     dateOfSale: "2026-01-03",
//     status: "Active",
//     warrantyPrice: 799,
//     earnings: 220,
//   },
//   {
//     warrantyId: "WRT-120047",
//     customerId: "CUST-90013",
//     customerName: "Neha Jadhav",
//     productName: "Screen Protection",
//     dateOfSale: "2026-01-03",
//     status: "Provisioning",
//     warrantyPrice: 499,
//     earnings: 0,
//   },
//   {
//     warrantyId: "WRT-120048",
//     customerId: "CUST-90014",
//     customerName: "Sameer Shaikh",
//     productName: "Total Protection",
//     dateOfSale: "2026-01-05",
//     status: "Rejected",
//     warrantyPrice: 999,
//     earnings: 0,
//   },
//   {
//     warrantyId: "WRT-120049",
//     customerId: "CUST-90015",
//     customerName: "Ayesha Khan",
//     productName: "Extended Warranty",
//     dateOfSale: "2026-01-06",
//     status: "Active",
//     warrantyPrice: 999,
//     earnings: 260,
//   },
//   {
//     warrantyId: "WRT-120050",
//     customerId: "CUST-90016",
//     customerName: "Irfan Khan",
//     productName: "Screen Protection",
//     dateOfSale: "2026-01-08",
//     status: "Active",
//     warrantyPrice: 599,
//     earnings: 190,
//   },
//   {
//     warrantyId: "WRT-120051",
//     customerId: "CUST-90017",
//     customerName: "Priya Singh",
//     productName: "Total Protection",
//     dateOfSale: "2026-01-10",
//     status: "Provisioning",
//     warrantyPrice: 1099,
//     earnings: 0,
//   },
//   {
//     warrantyId: "WRT-120052",
//     customerId: "CUST-90018",
//     customerName: "Sanjay More",
//     productName: "Extended Warranty",
//     dateOfSale: "2026-01-11",
//     status: "Active",
//     warrantyPrice: 699,
//     earnings: 210,
//   },
//   {
//     warrantyId: "WRT-120053",
//     customerId: "CUST-90019",
//     customerName: "Komal Desai",
//     productName: "Total Protection",
//     dateOfSale: "2026-01-12",
//     status: "Active",
//     warrantyPrice: 1299,
//     earnings: 320,
//   },
//   {
//     warrantyId: "WRT-120054",
//     customerId: "CUST-90020",
//     customerName: "Vikas Patel",
//     productName: "Screen Protection",
//     dateOfSale: "2026-01-13",
//     status: "Rejected",
//     warrantyPrice: 499,
//     earnings: 0,
//   },
//   {
//     warrantyId: "WRT-120055",
//     customerId: "CUST-90021",
//     customerName: "Pooja Nair",
//     productName: "Extended Warranty",
//     dateOfSale: "2026-01-14",
//     status: "Provisioning",
//     warrantyPrice: 899,
//     earnings: 0,
//   },
//   {
//     warrantyId: "WRT-120056",
//     customerId: "CUST-90022",
//     customerName: "Ritesh Gupta",
//     productName: "Total Protection",
//     dateOfSale: "2026-01-15",
//     status: "Active",
//     warrantyPrice: 1499,
//     earnings: 360,
//   },
// ];

// const STATUS_ORDER = ["Active", "Provisioning", "Rejected"];

// function sumBy(arr, key) {
//   return arr.reduce((acc, it) => acc + Number(it?.[key] || 0), 0);
// }

// function groupBy(arr, keyFn) {
//   const m = new Map();
//   for (const it of arr) {
//     const k = keyFn(it);
//     if (!m.has(k)) m.set(k, []);
//     m.get(k).push(it);
//   }
//   return m;
// }

// function KpiCard({ title, count, amount, icon, hint }) {
//   return (
//     <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
//       <div className="flex items-start justify-between gap-3">
//         <div>
//           <div className="text-xs font-semibold text-slate-500">{title}</div>
//           <div className="mt-1 flex items-baseline gap-2">
//             <div className="text-2xl font-semibold tracking-tight text-slate-700">{count}</div>
//             <div className="text-xs font-semibold text-slate-400">warranties</div>
//           </div>
//           <div className="mt-1 text-sm font-semibold text-blue-700">{moneyINR(amount)}</div>
//           {hint ? <div className="mt-1 text-[11px] font-semibold text-slate-400">{hint}</div> : null}
//         </div>
//         <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
//           <Icon name={icon} className="text-blue-700" />
//         </div>
//       </div>
//     </div>
//   );
// }

// function Card({ title, subtitle, right, children }) {
//   return (
//     <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
//       <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-5">
//         <div>
//           <div className="text-sm font-semibold text-slate-700">{title}</div>
//           {subtitle ? <div className="mt-0.5 text-xs font-semibold text-slate-500">{subtitle}</div> : null}
//         </div>
//         {right}
//       </div>
//       <div className="p-5">{children}</div>
//     </div>
//   );
// }

// function BarChart({ data, valueKey = "value", labelKey = "label", height = 220 }) {
//   const maxV = Math.max(1, ...data.map((d) => Number(d[valueKey] || 0)));
//   const pad = 12;
//   const barW = 36;
//   const gap = 18;
//   const w = pad * 2 + data.length * barW + (data.length - 1) * gap;

//   return (
//     <div className="w-full overflow-x-auto">
//       <svg width={Math.max(w, 520)} height={height} className="block">
//         <line
//           x1={pad}
//           y1={height - 36}
//           x2={Math.max(w, 520) - pad}
//           y2={height - 36}
//           stroke="rgb(226 232 240)"
//           strokeWidth="2"
//         />
//         {data.map((d, i) => {
//           const v = Number(d[valueKey] || 0);
//           const h = Math.round(((height - 64) * v) / maxV);
//           const x = pad + i * (barW + gap);
//           const y = height - 36 - h;
//           const label = String(d[labelKey] || "");
//           const note = d.note ? String(d.note) : "";
//           return (
//             <g key={label + i}>
//               <rect x={x} y={y} width={barW} height={h} rx={12} fill="rgb(37 99 235)" opacity="0.92" />
//               <text x={x + barW / 2} y={y - 8} textAnchor="middle" fontSize="11" fill="rgb(15 23 42)" fontWeight="700">
//                 {v === 0 ? "0" : ""}
//               </text>
//               <text
//                 x={x + barW / 2}
//                 y={height - 16}
//                 textAnchor="middle"
//                 fontSize="11"
//                 fill="rgb(100 116 139)"
//                 fontWeight="700"
//               >
//                 {label.length > 10 ? label.slice(0, 10) + "…" : label}
//               </text>
//               {note ? (
//                 <text
//                   x={x + barW / 2}
//                   y={height - 2}
//                   textAnchor="middle"
//                   fontSize="10"
//                   fill="rgb(148 163 184)"
//                   fontWeight="600"
//                 >
//                   {note}
//                 </text>
//               ) : null}
//             </g>
//           );
//         })}
//       </svg>
//     </div>
//   );
// }

// function HorizontalBars({ data }) {
//   const maxV = Math.max(1, ...data.map((d) => Number(d.value || 0)));
//   return (
//     <div className="w-full">
//       <div className="space-y-3">
//         {data.map((d) => {
//           const pct = clamp((Number(d.value || 0) / maxV) * 100, 0, 100);
//           return (
//             <div key={d.label} className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-200">
//               <div className="flex items-center justify-between gap-3">
//                 <div className="min-w-0">
//                   <div className="truncate text-sm font-semibold text-slate-700">{d.label}</div>
//                   <div className="text-[11px] font-semibold text-slate-500">{d.note}</div>
//                 </div>
//                 <div className="shrink-0 text-sm font-semibold text-blue-700">{moneyINR(d.value)}</div>
//               </div>
//               <div className="mt-2 h-3 w-full rounded-full bg-white ring-1 ring-slate-200">
//                 <div className="h-3 rounded-full bg-blue-600" style={{ width: pct + "%" }} />
//               </div>
//             </div>
//           );
//         })}
//       </div>
//     </div>
//   );
// }

// function StatusHelp() {
//   return (
//     <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
//       <div className="flex items-start gap-3">
//         <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
//           <Icon name="info" className="text-blue-700" />
//         </div>
//         <div className="min-w-0">
//           <div className="text-sm font-semibold text-slate-700">Status meaning</div>
//           <div className="mt-1 text-xs font-semibold text-slate-600 leading-relaxed">
//             <span className="font-semibold text-amber-700">Provisioning</span> = Customer can avail benefits, but earnings are pending. •{" "}
//             <span className="font-semibold text-rose-700">Rejected</span> = Warranty rejected, you lose earnings.
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default function RetailerWarrantyEarningsDashboard() {

//   const [apiSummary, setApiSummary] = useState({
//   total: { count: 0, payout: 0 },
//   active: { count: 0, payout: 0 },
//   provisioning: { count: 0, payout: 0 },
//   rejected: { count: 0, payout: 0 },
// });

// const [statusDonutData, setStatusDonutData] = useState({
//   Active: 0,
//   Provisioning: 0,
//   Rejected: 0,
// });

// const [productChart, setProductChart] = useState([]);

// const [loading, setLoading] = useState(true);


//   const [rows] = useState(MOCK_WARRANTIES);
//   const [q, setQ] = useState("");
//   const [statusFilter, setStatusFilter] = useState("ALL");

//   const filtered = useMemo(() => {
//     const term = q.trim().toLowerCase();
//     return rows
//       .filter((r) => (statusFilter === "ALL" ? true : r.status === statusFilter))
//       .filter((r) => {
//         if (!term) return true;
//         const hay = [r.warrantyId, r.customerId, r.customerName, r.productName, r.status, r.dateOfSale].join(" ").toLowerCase();
//         return hay.includes(term);
//       });
//   }, [rows, q, statusFilter]);

//   const totals = useMemo(() => {
//     const totalCount = rows.length;
//     const totalEarnings = sumBy(rows, "earnings");
//     const byStatus = {};
//     for (const s of STATUS_ORDER) {
//       const list = rows.filter((r) => r.status === s);
//       byStatus[s] = { count: list.length, earnings: sumBy(list, "earnings") };
//     }
//     return { totalCount, totalEarnings, byStatus };
//   }, [rows]);

//   const statusEarningsChart = useMemo(() => {
//     return STATUS_ORDER.map((s) => ({
//       label: s,
//       value: totals.byStatus[s]?.earnings || 0,
//       note: `${totals.byStatus[s]?.count || 0}w`,
//     }));
//   }, [totals]);

//   const statusCountChart = useMemo(() => {
//     return STATUS_ORDER.map((s) => ({
//       label: s,
//       value: totals.byStatus[s]?.count || 0,
//       note: moneyINR(totals.byStatus[s]?.earnings || 0),
//     }));
//   }, [totals]);

//  useEffect(() => {
//   const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
//   const role = authUser.role;

//   let params = {};

//   if (role === 5) {
//     params = {
//       retailer_id: authUser.id,
//       company_id: authUser.company_id,
//     };
//   } else if (role === 4) {
//     params = {
//       agent_id: authUser.id,
//       company_id: authUser.company_id,
//     };
//   } else if (role === 2) {
//     params = {
//       company_id: authUser.company_id,
//     };
//   }

//   if (!params.company_id) {
//     setLoading(false);
//     return;
//   }

//   fetch(`${BASE_URL}warranty/earning-dashbard?${new URLSearchParams(params)}`)
//     .then((r) => r.json())
//     .then((res) => {
//       const d = res?.data;
//       if (!d) return;

//       setApiSummary(d.summary);

//       const map = {};
//       d.status_chart?.forEach((s) => {
//         map[s.label] = s.count;
//       });
//       setStatusDonutData({
//         Active: map.Active || 0,
//         Provisioning: map.Provisioning || 0,
//         Rejected: map.Rejected || 0,
//       });

//       setProductChart(
//         d.product_wise_earnings?.map((p) => ({
//           label: p.product_name,
//           value: p.total_payout,
//           note: `${p.warranty_count} warranties`,
//         })) || []
//       );
//     })
//     .catch(() => {})
//     .finally(() => setLoading(false));
// }, []);



//   // const productChart = useMemo(() => {
//   //   const g = groupBy(rows, (r) => r.productName);
//   //   const out = [];
//   //   for (const [product, list] of g.entries()) {
//   //     out.push({ label: product, value: sumBy(list, "earnings"), note: `${list.length} warranties` });
//   //   }
//   //   out.sort((a, b) => b.value - a.value);
//   //   return out;
//   // }, [rows]);

//   const filteredMeta = useMemo(() => {
//     return { count: filtered.length, earnings: sumBy(filtered, "earnings"), price: sumBy(filtered, "warrantyPrice") };
//   }, [filtered]);

//   return (
//       <div className="mx-auto px-4 py-6">
//         <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
//           <KpiCard
//             title="Total Warranty"
//             count={loading ? <ValueLoader/> : apiSummary.total.count}
//             amount={loading ? <ValueLoader/> : apiSummary.total.payout}
//             icon="shield"
//             hint="All statuses"
            
//           />

//           <KpiCard
//             title="Active"
//             count={loading ? <ValueLoader/> : apiSummary.active.count}
//             amount={loading ? <ValueLoader/> : apiSummary.active.payout}
//             icon="wallet"
//             hint="Earnings credited"
//           />

//           <KpiCard
//             title="Provisioning"
//             count={loading ? <ValueLoader/> : apiSummary.provisioning.count}
//             amount={loading ? <ValueLoader/> : apiSummary.provisioning.payout}
//             icon="box"
//             hint="Benefits available • Earnings pending"
//           />

//           <KpiCard
//             title="Rejected"
//             count={loading ? <ValueLoader/> : apiSummary.rejected.count}
//             amount={loading ? <ValueLoader/> : apiSummary.rejected.payout}
//             icon="shield"
//             hint="Rejected • Earnings loss"
//           />

//         </div>

        

//         <div className="mt-5 grid gap-4 lg:grid-cols-5">
//           <div className="lg:col-span-3">
//             {/* <Card
//               title="Status-wise (Earnings + Count)"
//               subtitle="Active / Provisioning / Rejected"
//               right={
//                 <Badge tone="navy">
//                   <Icon name="chart" className="h-4 w-4" />
//                   Charts
//                 </Badge>
//               }
//             >
//               <div className="mb-3 flex flex-wrap items-center gap-2">
//                 <StatusPill status="Active" />
//                 <StatusPill status="Provisioning" />
//                 <StatusPill status="Rejected" />
//               </div>

//               <div className="space-y-4">
//                 <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
//                   <div className="mb-2 text-xs font-semibold text-slate-700">Earnings (₹)</div>
//                   <BarChart data={statusEarningsChart} valueKey="value" labelKey="label" height={210} />
//                 </div>

//                 <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
//                   <div className="mb-2 text-xs font-semibold text-slate-700">Count (warranties)</div>
//                   <BarChart data={statusCountChart} valueKey="value" labelKey="label" height={210} />
//                 </div>
//               </div>
//             </Card> */}
//             <StatusDonutChart mode="count" data={statusDonutData} />

//           </div>

//           <div className="lg:col-span-2">
//             <Card
//               title="Product-wise Earnings"
//               subtitle="Which product gives more earnings"
//               right={
//                 <Badge tone="navy">
//                   <Icon name="box" className="h-4 w-4" />
//                   Ranking
//                 </Badge>
//               }
//             >
//               <HorizontalBars data={productChart} />
//             </Card>
//           </div>
//         </div>

//         <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
//           <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
//             <div className="flex items-center gap-2">
//               <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
//                 <Icon name="search" className="text-blue-700" />
//               </div>
//               <div>
//                 <div className="text-sm font-semibold text-slate-700">Search</div>
//                 <div className="text-xs text-slate-500">Warranty ID / Customer / Product / Status / Date</div>
//               </div>
//             </div>

//             <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row lg:items-center lg:justify-end">
//               <div className="relative w-full lg:w-[360px]">
//                 <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
//                   <Icon name="search" className="h-4 w-4" />
//                 </div>
//                 <input
//                   value={q}
//                   onChange={(e) => setQ(e.target.value)}
//                   placeholder="Search warranty / customer..."
//                   className="w-full rounded-2xl border border-slate-200 bg-white px-10 py-2.5 text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400 focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
//                 />
//               </div>

//               <select
//                 value={statusFilter}
//                 onChange={(e) => setStatusFilter(e.target.value)}
//                 className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-blue-300 focus:ring-4 focus:ring-blue-100 lg:w-[190px]"
//               >
//                 <option value="ALL">All Status</option>
//                 <option value="Active">Active</option>
//                 <option value="Provisioning">Provisioning</option>
//                 <option value="Rejected">Rejected</option>
//               </select>

//               <div className="rounded-2xl bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-200">
//                 {filteredMeta.count} shown • <span className="text-blue-700">{moneyINR(filteredMeta.earnings)}</span>
//               </div>
//             </div>
//           </div>
//         </div>

//         <div className="mt-5 rounded-xl border border-slate-200 bg-white shadow-sm">
//           <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-5">
//             <div>
//               <div className="text-sm font-semibold text-slate-700">Warranty Earnings List</div>
//               <div className="mt-0.5 text-xs font-semibold text-slate-500">
//                 Warranty ID • Customer • Product • Sale date • Status • Price • Earnings
//               </div>
//             </div>
//             <Badge tone="blue">
//               <Icon name="shield" className="h-4 w-4" />
//               {filtered.length} Records
//             </Badge>
//           </div>

//           <div className="overflow-auto m-3">
//             <table className=" w-full m-2">
//               <thead className="bg-slate-50">
//                 <tr className="text-left">
//                   <Th>Warranty ID</Th>
//                   <Th>Customer</Th>
//                   <Th>Product</Th>
//                   <Th>Date of Sale</Th>
//                   <Th>Status</Th>
//                   <Th className="text-right">Warranty Price</Th>
//                   <Th className="text-right">Earnings</Th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {filtered.map((r) => (
//                   <tr
//                     key={r.warrantyId}
//                     className={cn(
//                       "border-t hover:bg-slate-50/60",
//                       r.status === "Provisioning" ? "border-amber-100 bg-amber-50/40" : "border-slate-100",
//                       r.status === "Rejected" ? "border-rose-100 bg-rose-50/40" : ""
//                     )}
//                   >
//                     <Td>
//                       <div className="text-sm font-semibold text-slate-700">{r.warrantyId}</div>
//                       <div
//                         className={cn(
//                           "text-[11px] font-semibold",
//                           r.status === "Rejected" ? "text-rose-600" : r.status === "Provisioning" ? "text-amber-700" : "text-slate-400"
//                         )}
//                       >
//                         {r.status === "Provisioning"
//                           ? "Benefits available • Earnings pending"
//                           : r.status === "Rejected"
//                           ? "Rejected • Earnings loss"
//                           : "Retailer"}
//                       </div>
//                     </Td>

//                     <Td>
//                       <div className="flex items-center gap-2">
//                         <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-800 font-semibold ring-1 ring-slate-200">
//                           {initials(r.customerName)}
//                         </span>
//                         <div className="min-w-0">
//                           <div className="truncate text-sm font-semibold text-slate-700">{r.customerName}</div>
//                           <div className="text-[11px] font-semibold text-slate-400">{r.customerId}</div>
//                         </div>
//                       </div>
//                     </Td>

//                     <Td>
//                       <div className="flex items-center gap-2">
//                         <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-blue-600 text-white font-semibold">
//                           {initials(r.productName)}
//                         </span>
//                         <div className="min-w-0">
//                           <div className="truncate text-sm font-semibold text-slate-700">{r.productName}</div>
//                           <div className="text-[11px] font-semibold text-slate-400">Plan</div>
//                         </div>
//                       </div>
//                     </Td>

//                     <Td>
//                       <div className="text-sm font-semibold text-slate-700">{toISODate(r.dateOfSale)}</div>
//                       <div className="text-[11px] font-semibold text-slate-400">Sale date</div>
//                     </Td>

//                     <Td>
//                       <StatusPill status={r.status} />
//                     </Td>

//                     <Td className="text-right">
//                       <div className="text-sm font-semibold text-slate-700">{moneyINR(r.warrantyPrice)}</div>
//                       <div className="text-[11px] font-semibold text-slate-400">MRP</div>
//                     </Td>

//                     <Td className="text-right">
//                       <div
//                         className={cn(
//                           "text-sm font-semibold",
//                           r.status === "Rejected"
//                             ? "text-rose-700"
//                             : r.status === "Provisioning"
//                             ? "text-amber-800"
//                             : r.earnings > 0
//                             ? "text-blue-700"
//                             : "text-slate-500"
//                         )}
//                       >
//                         {moneyINR(r.earnings)}
//                       </div>
//                       <div
//                         className={cn(
//                           "text-[11px] font-semibold",
//                           r.status === "Rejected" ? "text-rose-600" : r.status === "Provisioning" ? "text-amber-700" : "text-slate-400"
//                         )}
//                       >
//                         {r.status === "Provisioning" ? "Pending" : r.status === "Rejected" ? "Lost" : "Earning"}
//                       </div>
//                     </Td>
//                   </tr>
//                 ))}

//                 {filtered.length === 0 ? (
//                   <tr>
//                     <td colSpan={7} className="p-10">
//                       <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
//                         <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white">
//                           <Icon name="search" className="text-white" />
//                         </div>
//                         <div className="text-sm font-semibold text-slate-700">No records found</div>
//                         <div className="mt-1 text-xs font-semibold text-slate-500">Try different search or filter.</div>
//                       </div>
//                     </td>
//                   </tr>
//                 ) : null}
//               </tbody>
//             </table>
//           </div>

//           <div className="border-t border-slate-100 p-4">
//             <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
//               <div className="text-xs font-semibold text-slate-500">Showing {filtered.length} of {rows.length}</div>
//               <div className="flex flex-wrap items-center gap-2">
//                 <span className="rounded-full bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
//                   Total Price: {moneyINR(filteredMeta.price)}
//                 </span>
//                 <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 ring-1 ring-blue-200">
//                   Total Earnings: {moneyINR(filteredMeta.earnings)}
//                 </span>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* <div className="mt-6 text-center text-xs font-semibold text-slate-400">
//           Tip: Replace MOCK_WARRANTIES with API response ({"{ warrantyId, customerId, customerName, productName, dateOfSale, status, warrantyPrice, earnings }"}).
//         </div> */}
//       </div>
//   );
// }

// function Th({ children, className }) {
//   return <th className={cn("px-5 py-3 text-xs font-semibold text-slate-600", className)}>{children}</th>;
// }

// function Td({ children, className }) {
//   return <td className={cn("px-5 py-4 align-middle", className)}>{children}</td>;
// }




// import React, { useMemo, useState } from "react";
// import StatusDonutChart from "./StatusDonutChart";

// /**
//  * Retailer Warranty Earnings Dashboard
//  * React + Tailwind (JavaScript) | Theme: Blue & White
//  *
//  * Updates added:
//  * ✅ Customer Name + Customer ID
//  * ✅ Provisioning & Rejected highlight (Provisioning = benefits available, earnings pending; Rejected = earnings loss)
//  * ✅ Charts include Active/Provisioning/Rejected for BOTH Earnings + Count
//  */

// function cn(...classes) {
//   return classes.filter(Boolean).join(" ");
// }

// function moneyINR(n) {
//   const v = Number(n || 0);
//   try {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//       maximumFractionDigits: 0,
//     }).format(v);
//   } catch {
//     return `₹${Math.round(v).toLocaleString("en-IN")}`;
//   }
// }

// function toISODate(d) {
//   return String(d || "").slice(0, 10);
// }

// function clamp(n, a, b) {
//   return Math.max(a, Math.min(b, n));
// }

// function initials(name) {
//   const s = String(name || "").trim();
//   if (!s) return "U";
//   const parts = s.split(/\s+/).slice(0, 2);
//   return parts.map((p) => (p[0] ? p[0].toUpperCase() : "")).join("");
// }

// function Icon({ name, className }) {
//   const common = "h-5 w-5";
//   switch (name) {
//     case "wallet":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path
//             d="M3.5 7.5A3.5 3.5 0 017 4h12.5a1.5 1.5 0 010 3H7A2 2 0 005 9v8a3 3 0 003 3h12.5a1.5 1.5 0 000-3H8a2 2 0 01-2-2"
//             stroke="currentColor"
//             strokeWidth="1.8"
//             strokeLinejoin="round"
//           />
//           <path d="M16.5 12.5h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//         </svg>
//       );
//     case "shield":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path
//             d="M12 2l8 4v6c0 5.25-3.2 9.95-8 10-4.8-.05-8-4.75-8-10V6l8-4z"
//             stroke="currentColor"
//             strokeWidth="1.8"
//             strokeLinejoin="round"
//           />
//         </svg>
//       );
//     case "chart":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path d="M4 19V5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//           <path d="M4 19h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//           <path
//             d="M7 16l3-4 3 2 4-6 2 3"
//             stroke="currentColor"
//             strokeWidth="1.8"
//             strokeLinecap="round"
//             strokeLinejoin="round"
//           />
//         </svg>
//       );
//     case "box":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path d="M12 2l9 5-9 5-9-5 9-5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
//           <path d="M21 7v10l-9 5-9-5V7" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
//           <path d="M12 12v10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//         </svg>
//       );
//     case "search":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path d="M10.5 18a7.5 7.5 0 117.5-7.5A7.5 7.5 0 0110.5 18z" stroke="currentColor" strokeWidth="1.8" />
//           <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//         </svg>
//       );
//     case "info":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path d="M12 22a10 10 0 110-20 10 10 0 010 20z" stroke="currentColor" strokeWidth="1.8" />
//           <path d="M12 10.5v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//           <path d="M12 7.5h.01" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
//         </svg>
//       );
//     default:
//       return null;
//   }
// }

// function Badge({ tone = "blue", children }) {
//   const tones = {
//     blue: "bg-blue-50 text-blue-700 ring-blue-200",
//     green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
//     amber: "bg-amber-50 text-amber-800 ring-amber-200",
//     red: "bg-rose-50 text-rose-700 ring-rose-200",
//     navy: "bg-slate-50 text-slate-700 ring-slate-200",
//   };
//   return (
//     <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1", tones[tone] || tones.blue)}>
//       {children}
//     </span>
//   );
// }

// function StatusPill({ status }) {
//   const s = String(status || "");
//   const map = {
//     Active: { tone: "green", label: "Active" },
//     Provisioning: { tone: "amber", label: "Provisioning" },
//     Rejected: { tone: "red", label: "Rejected" },
//   };
//   const cfg = map[s] || { tone: "navy", label: s || "-" };
//   return <Badge tone={cfg.tone}>{cfg.label}</Badge>;
// }

// const MOCK_WARRANTIES = [
//   {
//     warrantyId: "WRT-120045",
//     customerId: "CUST-90011",
//     customerName: "Amit Patil",
//     productName: "Screen Protection",
//     dateOfSale: "2026-01-02",
//     status: "Active",
//     warrantyPrice: 499,
//     earnings: 160,
//   },
//   {
//     warrantyId: "WRT-120046",
//     customerId: "CUST-90012",
//     customerName: "Rahul Sharma",
//     productName: "Extended Warranty",
//     dateOfSale: "2026-01-03",
//     status: "Active",
//     warrantyPrice: 799,
//     earnings: 220,
//   },
//   {
//     warrantyId: "WRT-120047",
//     customerId: "CUST-90013",
//     customerName: "Neha Jadhav",
//     productName: "Screen Protection",
//     dateOfSale: "2026-01-03",
//     status: "Provisioning",
//     warrantyPrice: 499,
//     earnings: 0,
//   },
//   {
//     warrantyId: "WRT-120048",
//     customerId: "CUST-90014",
//     customerName: "Sameer Shaikh",
//     productName: "Total Protection",
//     dateOfSale: "2026-01-05",
//     status: "Rejected",
//     warrantyPrice: 999,
//     earnings: 0,
//   },
//   {
//     warrantyId: "WRT-120049",
//     customerId: "CUST-90015",
//     customerName: "Ayesha Khan",
//     productName: "Extended Warranty",
//     dateOfSale: "2026-01-06",
//     status: "Active",
//     warrantyPrice: 999,
//     earnings: 260,
//   },
//   {
//     warrantyId: "WRT-120050",
//     customerId: "CUST-90016",
//     customerName: "Irfan Khan",
//     productName: "Screen Protection",
//     dateOfSale: "2026-01-08",
//     status: "Active",
//     warrantyPrice: 599,
//     earnings: 190,
//   },
//   {
//     warrantyId: "WRT-120051",
//     customerId: "CUST-90017",
//     customerName: "Priya Singh",
//     productName: "Total Protection",
//     dateOfSale: "2026-01-10",
//     status: "Provisioning",
//     warrantyPrice: 1099,
//     earnings: 0,
//   },
//   {
//     warrantyId: "WRT-120052",
//     customerId: "CUST-90018",
//     customerName: "Sanjay More",
//     productName: "Extended Warranty",
//     dateOfSale: "2026-01-11",
//     status: "Active",
//     warrantyPrice: 699,
//     earnings: 210,
//   },
//   {
//     warrantyId: "WRT-120053",
//     customerId: "CUST-90019",
//     customerName: "Komal Desai",
//     productName: "Total Protection",
//     dateOfSale: "2026-01-12",
//     status: "Active",
//     warrantyPrice: 1299,
//     earnings: 320,
//   },
//   {
//     warrantyId: "WRT-120054",
//     customerId: "CUST-90020",
//     customerName: "Vikas Patel",
//     productName: "Screen Protection",
//     dateOfSale: "2026-01-13",
//     status: "Rejected",
//     warrantyPrice: 499,
//     earnings: 0,
//   },
//   {
//     warrantyId: "WRT-120055",
//     customerId: "CUST-90021",
//     customerName: "Pooja Nair",
//     productName: "Extended Warranty",
//     dateOfSale: "2026-01-14",
//     status: "Provisioning",
//     warrantyPrice: 899,
//     earnings: 0,
//   },
//   {
//     warrantyId: "WRT-120056",
//     customerId: "CUST-90022",
//     customerName: "Ritesh Gupta",
//     productName: "Total Protection",
//     dateOfSale: "2026-01-15",
//     status: "Active",
//     warrantyPrice: 1499,
//     earnings: 360,
//   },
// ];

// const STATUS_ORDER = ["Active", "Provisioning", "Rejected"];

// function sumBy(arr, key) {
//   return arr.reduce((acc, it) => acc + Number(it?.[key] || 0), 0);
// }

// function groupBy(arr, keyFn) {
//   const m = new Map();
//   for (const it of arr) {
//     const k = keyFn(it);
//     if (!m.has(k)) m.set(k, []);
//     m.get(k).push(it);
//   }
//   return m;
// }

// function KpiCard({ title, count, amount, icon, hint }) {
//   return (
//     <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
//       <div className="flex items-start justify-between gap-3">
//         <div>
//           <div className="text-xs font-semibold text-slate-500">{title}</div>
//           <div className="mt-1 flex items-baseline gap-2">
//             <div className="text-2xl font-semibold tracking-tight text-slate-700">{count}</div>
//             <div className="text-xs font-semibold text-slate-400">warranties</div>
//           </div>
//           <div className="mt-1 text-sm font-semibold text-blue-700">{moneyINR(amount)}</div>
//           {hint ? <div className="mt-1 text-[11px] font-semibold text-slate-400">{hint}</div> : null}
//         </div>
//         <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
//           <Icon name={icon} className="text-blue-700" />
//         </div>
//       </div>
//     </div>
//   );
// }

// function Card({ title, subtitle, right, children }) {
//   return (
//     <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
//       <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-5">
//         <div>
//           <div className="text-sm font-semibold text-slate-700">{title}</div>
//           {subtitle ? <div className="mt-0.5 text-xs font-semibold text-slate-500">{subtitle}</div> : null}
//         </div>
//         {right}
//       </div>
//       <div className="p-5">{children}</div>
//     </div>
//   );
// }

// function BarChart({ data, valueKey = "value", labelKey = "label", height = 220 }) {
//   const maxV = Math.max(1, ...data.map((d) => Number(d[valueKey] || 0)));
//   const pad = 12;
//   const barW = 36;
//   const gap = 18;
//   const w = pad * 2 + data.length * barW + (data.length - 1) * gap;

//   return (
//     <div className="w-full overflow-x-auto">
//       <svg width={Math.max(w, 520)} height={height} className="block">
//         <line
//           x1={pad}
//           y1={height - 36}
//           x2={Math.max(w, 520) - pad}
//           y2={height - 36}
//           stroke="rgb(226 232 240)"
//           strokeWidth="2"
//         />
//         {data.map((d, i) => {
//           const v = Number(d[valueKey] || 0);
//           const h = Math.round(((height - 64) * v) / maxV);
//           const x = pad + i * (barW + gap);
//           const y = height - 36 - h;
//           const label = String(d[labelKey] || "");
//           const note = d.note ? String(d.note) : "";
//           return (
//             <g key={label + i}>
//               <rect x={x} y={y} width={barW} height={h} rx={12} fill="rgb(37 99 235)" opacity="0.92" />
//               <text x={x + barW / 2} y={y - 8} textAnchor="middle" fontSize="11" fill="rgb(15 23 42)" fontWeight="700">
//                 {v === 0 ? "0" : ""}
//               </text>
//               <text
//                 x={x + barW / 2}
//                 y={height - 16}
//                 textAnchor="middle"
//                 fontSize="11"
//                 fill="rgb(100 116 139)"
//                 fontWeight="700"
//               >
//                 {label.length > 10 ? label.slice(0, 10) + "…" : label}
//               </text>
//               {note ? (
//                 <text
//                   x={x + barW / 2}
//                   y={height - 2}
//                   textAnchor="middle"
//                   fontSize="10"
//                   fill="rgb(148 163 184)"
//                   fontWeight="600"
//                 >
//                   {note}
//                 </text>
//               ) : null}
//             </g>
//           );
//         })}
//       </svg>
//     </div>
//   );
// }

// function HorizontalBars({ data }) {
//   const maxV = Math.max(1, ...data.map((d) => Number(d.value || 0)));
//   return (
//     <div className="w-full">
//       <div className="space-y-3">
//         {data.map((d) => {
//           const pct = clamp((Number(d.value || 0) / maxV) * 100, 0, 100);
//           return (
//             <div key={d.label} className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-200">
//               <div className="flex items-center justify-between gap-3">
//                 <div className="min-w-0">
//                   <div className="truncate text-sm font-semibold text-slate-700">{d.label}</div>
//                   <div className="text-[11px] font-semibold text-slate-500">{d.note}</div>
//                 </div>
//                 <div className="shrink-0 text-sm font-semibold text-blue-700">{moneyINR(d.value)}</div>
//               </div>
//               <div className="mt-2 h-3 w-full rounded-full bg-white ring-1 ring-slate-200">
//                 <div className="h-3 rounded-full bg-blue-600" style={{ width: pct + "%" }} />
//               </div>
//             </div>
//           );
//         })}
//       </div>
//     </div>
//   );
// }

// function StatusHelp() {
//   return (
//     <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
//       <div className="flex items-start gap-3">
//         <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
//           <Icon name="info" className="text-blue-700" />
//         </div>
//         <div className="min-w-0">
//           <div className="text-sm font-semibold text-slate-700">Status meaning</div>
//           <div className="mt-1 text-xs font-semibold text-slate-600 leading-relaxed">
//             <span className="font-semibold text-amber-700">Provisioning</span> = Customer can avail benefits, but earnings are pending. •{" "}
//             <span className="font-semibold text-rose-700">Rejected</span> = Warranty rejected, you lose earnings.
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default function RetailerWarrantyEarningsDashboard() {
//   const [rows] = useState(MOCK_WARRANTIES);
//   const [q, setQ] = useState("");
//   const [statusFilter, setStatusFilter] = useState("ALL");

//   const filtered = useMemo(() => {
//     const term = q.trim().toLowerCase();
//     return rows
//       .filter((r) => (statusFilter === "ALL" ? true : r.status === statusFilter))
//       .filter((r) => {
//         if (!term) return true;
//         const hay = [r.warrantyId, r.customerId, r.customerName, r.productName, r.status, r.dateOfSale].join(" ").toLowerCase();
//         return hay.includes(term);
//       });
//   }, [rows, q, statusFilter]);

//   const totals = useMemo(() => {
//     const totalCount = rows.length;
//     const totalEarnings = sumBy(rows, "earnings");
//     const byStatus = {};
//     for (const s of STATUS_ORDER) {
//       const list = rows.filter((r) => r.status === s);
//       byStatus[s] = { count: list.length, earnings: sumBy(list, "earnings") };
//     }
//     return { totalCount, totalEarnings, byStatus };
//   }, [rows]);

//   const statusEarningsChart = useMemo(() => {
//     return STATUS_ORDER.map((s) => ({
//       label: s,
//       value: totals.byStatus[s]?.earnings || 0,
//       note: `${totals.byStatus[s]?.count || 0}w`,
//     }));
//   }, [totals]);

//   const statusCountChart = useMemo(() => {
//     return STATUS_ORDER.map((s) => ({
//       label: s,
//       value: totals.byStatus[s]?.count || 0,
//       note: moneyINR(totals.byStatus[s]?.earnings || 0),
//     }));
//   }, [totals]);

//   const productChart = useMemo(() => {
//     const g = groupBy(rows, (r) => r.productName);
//     const out = [];
//     for (const [product, list] of g.entries()) {
//       out.push({ label: product, value: sumBy(list, "earnings"), note: `${list.length} warranties` });
//     }
//     out.sort((a, b) => b.value - a.value);
//     return out;
//   }, [rows]);

//   const filteredMeta = useMemo(() => {
//     return { count: filtered.length, earnings: sumBy(filtered, "earnings"), price: sumBy(filtered, "warrantyPrice") };
//   }, [filtered]);

//   return (
//       <div className="mx-auto px-4 py-6">
//         <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
//           <KpiCard title="Total Warranty" count={totals.totalCount} amount={totals.totalEarnings} icon="shield" hint="All statuses" />
//           <KpiCard title="Active" count={totals.byStatus.Active.count} amount={totals.byStatus.Active.earnings} icon="wallet" hint="Earnings credited" />
//           <KpiCard
//             title="Provisioning"
//             count={totals.byStatus.Provisioning.count}
//             amount={totals.byStatus.Provisioning.earnings}
//             icon="box"
//             hint="Benefits available • Earnings pending"
//           />
//           <KpiCard
//             title="Rejected"
//             count={totals.byStatus.Rejected.count}
//             amount={totals.byStatus.Rejected.earnings}
//             icon="shield"
//             hint="Rejected • Earnings loss"
//           />
//         </div>

//         <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
//           <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
//             <div className="flex items-center gap-2">
//               <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
//                 <Icon name="search" className="text-blue-700" />
//               </div>
//               <div>
//                 <div className="text-sm font-semibold text-slate-700">Search</div>
//                 <div className="text-xs text-slate-500">Warranty ID / Customer / Product / Status / Date</div>
//               </div>
//             </div>

//             <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row lg:items-center lg:justify-end">
//               <div className="relative w-full lg:w-[360px]">
//                 <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
//                   <Icon name="search" className="h-4 w-4" />
//                 </div>
//                 <input
//                   value={q}
//                   onChange={(e) => setQ(e.target.value)}
//                   placeholder="Search warranty / customer..."
//                   className="w-full rounded-2xl border border-slate-200 bg-white px-10 py-2.5 text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400 focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
//                 />
//               </div>

//               <select
//                 value={statusFilter}
//                 onChange={(e) => setStatusFilter(e.target.value)}
//                 className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-blue-300 focus:ring-4 focus:ring-blue-100 lg:w-[190px]"
//               >
//                 <option value="ALL">All Status</option>
//                 <option value="Active">Active</option>
//                 <option value="Provisioning">Provisioning</option>
//                 <option value="Rejected">Rejected</option>
//               </select>

//               <div className="rounded-2xl bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-200">
//                 {filteredMeta.count} shown • <span className="text-blue-700">{moneyINR(filteredMeta.earnings)}</span>
//               </div>
//             </div>
//           </div>
//         </div>

//         <div className="mt-5 grid gap-4 lg:grid-cols-5">
//           <div className="lg:col-span-2">
//             {/* <Card
//               title="Status-wise (Earnings + Count)"
//               subtitle="Active / Provisioning / Rejected"
//               right={
//                 <Badge tone="navy">
//                   <Icon name="chart" className="h-4 w-4" />
//                   Charts
//                 </Badge>
//               }
//             >
//               <div className="mb-3 flex flex-wrap items-center gap-2">
//                 <StatusPill status="Active" />
//                 <StatusPill status="Provisioning" />
//                 <StatusPill status="Rejected" />
//               </div>

//               <div className="space-y-4">
//                 <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
//                   <div className="mb-2 text-xs font-semibold text-slate-700">Earnings (₹)</div>
//                   <BarChart data={statusEarningsChart} valueKey="value" labelKey="label" height={210} />
//                 </div>

//                 <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
//                   <div className="mb-2 text-xs font-semibold text-slate-700">Count (warranties)</div>
//                   <BarChart data={statusCountChart} valueKey="value" labelKey="label" height={210} />
//                 </div>
//               </div>
//             </Card> */}
//             <StatusDonutChart/>
//           </div>

//           <div className="lg:col-span-3">
//             <Card
//               title="Product-wise Earnings"
//               subtitle="Which product gives more earnings"
//               right={
//                 <Badge tone="navy">
//                   <Icon name="box" className="h-4 w-4" />
//                   Ranking
//                 </Badge>
//               }
//             >
//               <HorizontalBars data={productChart} />
//             </Card>
//           </div>
//         </div>

//         <div className="mt-5 rounded-xl border border-slate-200 bg-white shadow-sm">
//           <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-5">
//             <div>
//               <div className="text-sm font-semibold text-slate-700">Warranty Earnings List</div>
//               <div className="mt-0.5 text-xs font-semibold text-slate-500">
//                 Warranty ID • Customer • Product • Sale date • Status • Price • Earnings
//               </div>
//             </div>
//             <Badge tone="blue">
//               <Icon name="shield" className="h-4 w-4" />
//               {filtered.length} Records
//             </Badge>
//           </div>

//           <div className="overflow-auto">
//             <table className=" w-full">
//               <thead className="bg-slate-50">
//                 <tr className="text-left">
//                   <Th>Warranty ID</Th>
//                   <Th>Customer</Th>
//                   <Th>Product</Th>
//                   <Th>Date of Sale</Th>
//                   <Th>Status</Th>
//                   <Th className="text-right">Warranty Price</Th>
//                   <Th className="text-right">Earnings</Th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {filtered.map((r) => (
//                   <tr
//                     key={r.warrantyId}
//                     className={cn(
//                       "border-t hover:bg-slate-50/60",
//                       r.status === "Provisioning" ? "border-amber-100 bg-amber-50/40" : "border-slate-100",
//                       r.status === "Rejected" ? "border-rose-100 bg-rose-50/40" : ""
//                     )}
//                   >
//                     <Td>
//                       <div className="text-sm font-semibold text-slate-700">{r.warrantyId}</div>
//                       <div
//                         className={cn(
//                           "text-[11px] font-semibold",
//                           r.status === "Rejected" ? "text-rose-600" : r.status === "Provisioning" ? "text-amber-700" : "text-slate-400"
//                         )}
//                       >
//                         {r.status === "Provisioning"
//                           ? "Benefits available • Earnings pending"
//                           : r.status === "Rejected"
//                           ? "Rejected • Earnings loss"
//                           : "Retailer"}
//                       </div>
//                     </Td>

//                     <Td>
//                       <div className="flex items-center gap-2">
//                         <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-800 font-semibold ring-1 ring-slate-200">
//                           {initials(r.customerName)}
//                         </span>
//                         <div className="min-w-0">
//                           <div className="truncate text-sm font-semibold text-slate-700">{r.customerName}</div>
//                           <div className="text-[11px] font-semibold text-slate-400">{r.customerId}</div>
//                         </div>
//                       </div>
//                     </Td>

//                     <Td>
//                       <div className="flex items-center gap-2">
//                         <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-blue-600 text-white font-semibold">
//                           {initials(r.productName)}
//                         </span>
//                         <div className="min-w-0">
//                           <div className="truncate text-sm font-semibold text-slate-700">{r.productName}</div>
//                           <div className="text-[11px] font-semibold text-slate-400">Plan</div>
//                         </div>
//                       </div>
//                     </Td>

//                     <Td>
//                       <div className="text-sm font-semibold text-slate-700">{toISODate(r.dateOfSale)}</div>
//                       <div className="text-[11px] font-semibold text-slate-400">Sale date</div>
//                     </Td>

//                     <Td>
//                       <StatusPill status={r.status} />
//                     </Td>

//                     <Td className="text-right">
//                       <div className="text-sm font-semibold text-slate-700">{moneyINR(r.warrantyPrice)}</div>
//                       <div className="text-[11px] font-semibold text-slate-400">MRP</div>
//                     </Td>

//                     <Td className="text-right">
//                       <div
//                         className={cn(
//                           "text-sm font-semibold",
//                           r.status === "Rejected"
//                             ? "text-rose-700"
//                             : r.status === "Provisioning"
//                             ? "text-amber-800"
//                             : r.earnings > 0
//                             ? "text-blue-700"
//                             : "text-slate-500"
//                         )}
//                       >
//                         {moneyINR(r.earnings)}
//                       </div>
//                       <div
//                         className={cn(
//                           "text-[11px] font-semibold",
//                           r.status === "Rejected" ? "text-rose-600" : r.status === "Provisioning" ? "text-amber-700" : "text-slate-400"
//                         )}
//                       >
//                         {r.status === "Provisioning" ? "Pending" : r.status === "Rejected" ? "Lost" : "Earning"}
//                       </div>
//                     </Td>
//                   </tr>
//                 ))}

//                 {filtered.length === 0 ? (
//                   <tr>
//                     <td colSpan={7} className="p-10">
//                       <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
//                         <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white">
//                           <Icon name="search" className="text-white" />
//                         </div>
//                         <div className="text-sm font-semibold text-slate-700">No records found</div>
//                         <div className="mt-1 text-xs font-semibold text-slate-500">Try different search or filter.</div>
//                       </div>
//                     </td>
//                   </tr>
//                 ) : null}
//               </tbody>
//             </table>
//           </div>

//           <div className="border-t border-slate-100 p-4">
//             <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
//               <div className="text-xs font-semibold text-slate-500">Showing {filtered.length} of {rows.length}</div>
//               <div className="flex flex-wrap items-center gap-2">
//                 <span className="rounded-full bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
//                   Total Price: {moneyINR(filteredMeta.price)}
//                 </span>
//                 <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 ring-1 ring-blue-200">
//                   Total Earnings: {moneyINR(filteredMeta.earnings)}
//                 </span>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* <div className="mt-6 text-center text-xs font-semibold text-slate-400">
//           Tip: Replace MOCK_WARRANTIES with API response ({"{ warrantyId, customerId, customerName, productName, dateOfSale, status, warrantyPrice, earnings }"}).
//         </div> */}
//       </div>
//   );
// }

// function Th({ children, className }) {
//   return <th className={cn("px-5 py-3 text-xs font-semibold text-slate-600", className)}>{children}</th>;
// }

// function Td({ children, className }) {
//   return <td className={cn("px-5 py-4 align-middle", className)}>{children}</td>;
// }
